import { Company, CompanyUser, CompanyUserRole, CompanyFeatures, BillingTier } from '../../../core/models/Company'

// Extended interfaces for UI components
export interface CompanyWithStats extends Company {
  total_users?: number
  active_users?: number
  total_rooms?: number
  active_rooms?: number
}

export interface CompanyManagementPanelProps {
  onBack?: () => void
  isPlatformAdmin?: boolean
  isCompanyManagerOrAdmin?: boolean
  isGlobalAdmin?: boolean
  currentCompany?: Company
  // Optional: select initial tab when rendering within routed sub-sections
  // 'overview' | 'billing' | 'users' | 'settings' | 'wallet' | 'setup'
  initialCompanyTab?: 'overview' | 'billing' | 'users' | 'settings' | 'wallet' | 'setup'
}

export interface CompanyFormData {
  id?: string
  /** Create only: the lobby map of the new company (SC-422). */
  lobbyMapId?: string
  name: string
  domain: string
  subdomain: string
  platform_name: string
  platform_url: string
  logo_url: string
  primary_color: string
  secondary_color: string
  /** Persisted in Parse Company.settings (optional branding). */
  accent_color: string
  link_color: string
  surface_tint_color: string
  allowed_domains: string[]
  auto_assignment_enabled: boolean
  /** A legacy role string or a custom role's key (`companyDefaultRoleValue`). */
  default_role: CompanyUserRole | string
  max_users: number
  max_rooms: number
  billing_tier: BillingTier
  base_price: number
  price_per_user: number
  features: CompanyFeatures
  matrix_chat_enabled: boolean
  /** Matrix chat tenant slug (Company.matrix_chat_domain). */
  matrix_chat_domain?: string
}

import { UserForInvitation } from '../../../services/userSearchService'

export interface InviteUserFormData {
  email: string
  /** A legacy role string or a role key (a custom role's key, SC-439). */
  role: CompanyUserRole | string
  department: string
  title: string
  permissions: string[]
  selectedUser: UserForInvitation | null
  invitationType: 'email' | 'existing_user'
}

export interface EditUserFormData {
  /** A legacy role string or a custom role's key (`memberRoleValue`). */
  role: CompanyUserRole | string
  department: string
  title: string
  permissions: string[]
}

export interface DialogState {
  showCreateDialog: boolean
  showEditCompanyDialog: boolean
  showUsersDialog: boolean
  showInviteDialog: boolean
  showEditUserDialog: boolean
}

export interface LoadingState {
  loading: boolean
  loadingUsers: boolean
  loadingUserInfo: boolean
}

export interface UserMenuState {
  userMenuAnchor: HTMLElement | null
  selectedUserForMenu: CompanyUser | null
}

export interface SelectedItems {
  selectedCompany: CompanyWithStats | null
  selectedUser: CompanyUser | null
}

// Available permissions for users
export const AVAILABLE_PERMISSIONS = [
  'mapEditor',
  'chat',
  'whiteboard',
  'screenSharing',
  'videoCalling',
  'createRooms',
  'inviteUsers',
  'manageUsers',
  'viewAnalytics',
  'customBranding',
] as const

export type Permission = typeof AVAILABLE_PERMISSIONS[number]

// Color mapping functions
export const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'success'
    case 'inactive':
      return 'error'
    case 'suspended':
      return 'warning'
    default:
      return 'default'
  }
}

export const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':
      return '#f44336'
    case 'manager':
      return '#ff9800'
    case 'moderator':
      return '#2196f3'
    case 'user':
    default:
      return '#4caf50'
  }
}

/**
 * @deprecated Use BillingUtils.getBillingTierColor instead
 */
export const getBillingTierColor = (tier: string | { color?: string; tier_key?: string }) => {
  // If tier is an object (new BillingTier from database), use its color
  if (typeof tier === 'object' && tier !== null) {
    if (tier.color) {
      return tier.color
    }
    // Fall back to tier_key if no color
    if (tier.tier_key) {
      return getBillingTierColor(tier.tier_key)
    }
    return '#757575'
  }
  
  // Handle string tier (old system or tier_key)
  switch (tier) {
    case 'free':
      return '#4caf50'
    case 'starter':
    case 'basic': // Handle legacy 'basic' value
      return '#2196f3'
    case 'professional':
      return '#ff9800'
    case 'enterprise':
      return '#9c27b0'
    case 'unlimited':
      return '#42eacb'
    case 'custom':
      return '#f44336'
    default:
      return '#757575'
  }
}

/**
 * @deprecated Use BillingUtils.normalizeBillingTier instead
 */
export const normalizeBillingTier = (tier: string) => {
  if (tier === 'basic') {
    return 'starter'
  }
  return tier
}

export const getUserEmail = (user: CompanyUser) => {
  return user.user?.email || user.user_id
}

export const renderUserPermissions = (permissions: string[]) => {
  if (!permissions || permissions.length === 0) {
    return null
  }

  return {
    displayPermissions: permissions.slice(0, 3),
    hasMore: permissions.length > 3,
    moreCount: permissions.length - 3
  }
} 